<template>
  <CCard>
    <CCardHeader class="vertical-center d-inline-flex flex-gap-2">
      <CIcon name="cil-user" />

      <h5 class="mb-0">
        {{ this.$route.meta.label }}
        <template v-if="user && !spinners.loading">
          : <h3 class="d-inline"><CBadge color="secondary">{{ user.first_name }}</CBadge></h3>
        </template>
      </h5>

      <div class="ml-auto">
        <CButton color="dark" :to="{ name: 'Couriers' }">
          <CIcon name="cil-arrow-thick-left" class="align-bottom" /> Back
        </CButton>
      </div>
    </CCardHeader>

    <CCardBody>
      <form-summary
        v-if="!isValid"
        class="form-errors alert alert-danger"
        :validator="$v.form"
        :attributes="attributes"
      />

      <CForm class="courier-form" v-if="!(spinners.loading)">
        <CTabs class="tab-menu" :active-tab.sync="activeTab" variant="pills" :vertical="{ navs: 'col-xl-2 col-md-3', content: 'col-xl-10 col-md-9' }">
          <CTab active>
            <template slot="title">
              <CIcon name="cil-task"/> General Information
            </template>

            <div class="d-flex flex-column">
              <div v-if="!itemId" class="mb-3 d-flex flex-column flex-md-row">
                <v-select
                  class="w-100"
                  :placeholder="users.length == 0 ? 'Search for a user..' : 'Select or search..'"
                  v-model="user"
                  :options="users"
                  :searchable="true"
                  :loading="spinners.searching && users.length == 0"
                  @search="searchUsers"
                  @input="handleUserInput"
                >
                  <template v-slot:option="user">
                    <div class="d-flex align-items-center">
                      <div class="c-avatar mr-2">
                        <img :src="user.image_icon || user.gravatar" class="c-avatar-img">
                      </div>
                      <span class="small"><strong>{{ user.first_name }}</strong> #{{ user.id }}  ({{ user.email }})</span>
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="user">
                    {{ user.first_name }} #{{ user.id }}
                  </template>
                  <span slot="no-options">Type to search users..</span>
                </v-select>
              </div>

              <template v-if="user">
                <!-- User Info -->
                <div class="d-flex flex-column flex-sm-row align-items-center justify-content-between mb-4 h-100">
                  <div class="flex-fill d-flex align-items-center flex-gap-2">
                    <CImg
                      :src="user.image_icon || user.gravatar"
                      class="rounded-circle bg-white"
                      style="width: 96px; height: 96px"
                      placeholderColor="lightgray"
                    />
                    <div>
                      <h2 class="mb-0">{{ user.first_name }}</h2>
                    </div>
                  </div>

                  <div class="p-2 mr-4 user-info">
                    <address class="mb-1 blockquote">
                      <strong>#{{ user.id }}</strong><br>
                      <CIcon name="cil-mobile" /> {{ form.detail.phone_number ?? user.mobile }}<br>
                      <CIcon name="cil-at" /> <a :href="`mailto:${user.email}`">{{ user.email }}</a>
                    </address>
                    <div class="button">
                      <CLink :to="{ name: 'Edit User', params: { id: user.id }, query: { tab: 0 } }">
                        <CIcon name="cil-pen" /> Edit User
                      </CLink>
                      <CLink
                        v-if="canViewUserDetails"
                        class="ml-3"
                        :to="{ name: 'User', params: { id: user.id } }"
                      >
                        <CIcon name="cil-user" /> View User
                      </CLink>
                    </div>
                  </div>
                </div>

                <div v-if="itemId" class="courier-location-summary mb-4">
                  <CIcon name="cil-location-pin" class="mr-1" />
                  <a
                    v-if="currentCourierLocationUrl"
                    :href="currentCourierLocationUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Last GPS {{ formatCourierLocationAge(currentCourierLocation) }}
                  </a>
                  <span v-else>
                    Last GPS {{ formatCourierLocationAge(currentCourierLocation) }}
                  </span>
                  <CBadge
                    v-if="currentCourierLocation"
                    class="ml-2"
                    :color="currentCourierLocation.is_stale ? 'warning' : 'success'"
                  >
                    {{ currentCourierLocation.is_stale ? 'Stale' : 'Live' }}
                  </CBadge>
                  <span v-if="currentCourierNearestHub" class="ml-2 text-muted">
                    Nearest hub: {{ currentCourierNearestHub.name }}
                    ({{ formatCourierDistance(currentCourierNearestHub.distance_meters) }})
                  </span>
                </div>

                <div class="d-flex flex-column flex-md-row flex-gap-2 mb-0">
                  <!-- Dynamic Hub (Gezgin Kurye) -->
                  <div class="form-group w-100">
                    <label class="d-block" style="font-weight: 500">Using Dynamic Hub?</label>
                    <CSwitch
                      :disabled="!hasFlexibleWorkingHours"
                      :checked.sync="$v.form.detail.is_dynamic_hub.$model"
                      labelOn="YES"
                      labelOff="NO"
                      color="info"
                      shape="pill"
                      variant="opposite"
                      size="lg"
                    />
                    <small v-if="form.detail.is_dynamic_hub" class="form-text text-muted">* Before starting a shift, this courier can now select the nearest hub based on location.</small>
                    <small v-else-if="!hasFlexibleWorkingHours" class="form-text text-warning">* Dynamic hub is only available for standard couriers with flexible working hours (per order contract).</small>
                    <small v-else class="form-text text-muted">* This courier will be manually assigned to specific hubs.</small>
                  </div>
                </div>

                <!-- Hubs -->
                <div class="form-group w-100 mb-4" :class="{ 'disable-item': form.detail.is_dynamic_hub }">
                  <label class="d-block">Hubs:</label>
                  <v-select
                    :disabled="form.detail.is_dynamic_hub"
                    placeholder="Hubs.."
                    label="name"
                    v-model="form.hubs"
                    :options="hubs"
                    :searchable="false"
                    :reduce="hub => hub.id"
                    multiple
                  >
                    <template v-slot:option="hub">
                      <strong>{{ hub.zip }}</strong> -
                      <span>{{ hub.name }} </span>
                    </template>
                  </v-select>
                </div>

                <!-- Vehicle Info -->
                <div class="d-flex flex-column flex-md-row mb-4">
                  <CSelect
                    class="w-100 mr-2"
                    label="Travel Mode:"
                    placeholder="Please select.."
                    :options="getTravelModes"
                    :value.sync="$v.form.vehicle.mode.$model"
                  />

                  <CInput
                    class="w-100"
                    label="Vehicle Plate:"
                    placeholder="License Plate"
                    :value.sync="$v.form.vehicle.license_plate.$model"
                    @input="val => { if (val.trim() === '') form.vehicle.license_plate = null }"
                  />
                </div>

                <!-- Personal Info -->
                <h5 class="pb-1" style="border-bottom: 2px solid #636f83;">Personal Information</h5>

                <div class="d-flex flex-column flex-md-row flex-gap-2 mb-2">
                  <CSelect
                    class="w-100"
                    label="Country:"
                    placeholder="Please select.."
                    :options="authCountries"
                    :value.sync="$v.form.detail.country_id.$model"
                  />
                  <CInput
                    class="w-100"
                    label="National Registration Number:"
                    placeholder="Rijksregisternummer"
                    :value.sync="$v.form.detail.reg_number.$model"
                    @input="value => setNullIfEmpty(value, 'detail.reg_number')"
                  />
                </div>

                <div class="d-flex flex-column flex-md-row flex-gap-2 mb-2">
                  <CInput
                    type="date"
                    class="w-100"
                    label="Birth Date:"
                    :value.sync="$v.form.detail.birth_date.$model"
                    :max="new Date().toISOString().slice(0,10)"
                  />
                  <CInput
                    class="w-100"
                    label="Phone Number:"
                    placeholder="Phone number"
                    :lazy="false"
                    :value.sync="$v.form.detail.phone_number.$model"
                  />
                </div>

                <div class="d-flex flex-column flex-md-row flex-gap-2 mb-2">
                  <CInput
                    class="w-100"
                    label="Bank Account Number (IBAN):"
                    placeholder="Bank Account Number (IBAN)"
                    :lazy="false"
                    :value.sync="$v.form.detail.iban.$model"
                    :isValid="checkIfValid('detail.iban')"
                  />
                  <CInput
                    class="w-100"
                    label="Bank Account Holder:"
                    placeholder="Account Holder Name"
                    :value.sync="$v.form.detail.account_holder_name.$model"
                  />
                </div>

                <section class="address">
                  <CInput
                    id="verify_address"
                    :value.sync="verified_address"
                    class="w-100"
                    label="Verify Address From Google:"
                    placeholder="Enter a location"
                    :lazy="false"
                  >
                    <template #prepend>
                      <CButton color="info"><CIcon name="cil-location-pin"/></CButton>
                    </template>
                  </CInput>

                  <div class="d-flex flex-column flex-md-row">
                    <CTextarea
                      class="w-100"
                      rows="2"
                      label="Address:"
                      placeholder="Street name and house number"
                      :lazy="false"
                      :value.sync="$v.form.detail.address.$model"
                    />
                  </div>

                  <div class="d-flex flex-column flex-md-row flex-gap-2">
                    <CInput
                      class="w-100"
                      label="Postal Code:"
                      placeholder="Postal code"
                      :value.sync="$v.form.detail.postal_code.$model"
                    />
                    <CInput
                      class="w-100"
                      label="City:"
                      placeholder="City"
                      :value.sync="$v.form.detail.city.$model"
                    />
                  </div>
                </section>
              </template>
            </div>
          </CTab>

          <CTab v-if="isEdit">
            <template slot="title">
              <CIcon name="cil-mobile"/> Devices
              <CBadge color="secondary" class="ml-1">{{ userDevices.length }}</CBadge>
            </template>

            <CCard id="courier-devices">
              <CCardHeader>
                <CIcon name="cil-mobile" class="align-bottom" />
                <strong> Courier Devices </strong>
                <small>({{ user.usersdevices_count || userDevices.length || 0 }})</small>
              </CCardHeader>
              <CCardBody>
                <CListGroup accent>
                  <CListGroupItem
                    v-for="device of userDevices"
                    :key="device.id"
                    accent="info"
                    class="device-list-item"
                  >
                    <div class="device-primary-row">
                      <div>
                        <strong>{{ formatDeviceTitle(device) }}</strong>
                        <span v-if="formatDeviceSecondary(device)" class="text-muted d-block">
                          {{ formatDeviceSecondary(device) }}
                        </span>
                      </div>
                      <CBadge :color="device.is_notify ? 'success' : 'secondary'">
                        {{ device.is_notify ? 'Notify On' : 'Notify Off' }}
                      </CBadge>
                    </div>

                    <div class="device-meta">
                      <div><strong>App:</strong> {{ formatDeviceApp(device) }}</div>
                      <div><strong>Device ID:</strong> {{ maskedDeviceId(device.device_id) }}</div>
                      <div><strong>Last Active:</strong> {{ formatDeviceSeenAt(device) }}</div>
                      <div v-if="formatDeviceLocation(device)"><strong>Location:</strong> {{ formatDeviceLocation(device) }}</div>
                      <div v-if="device.timezone"><strong>Timezone:</strong> {{ device.timezone }}</div>
                      <div v-if="device.country"><strong>Country:</strong> {{ device.country }}</div>
                      <div v-if="device.app_versions"><strong>App Version:</strong> {{ device.app_versions }}</div>
                    </div>
                  </CListGroupItem>

                  <CListGroupItem v-if="!userDevices.length" accent="secondary">
                    No linked devices
                  </CListGroupItem>
                </CListGroup>
              </CCardBody>
            </CCard>
          </CTab>
          
          <CTab v-if="isEdit">
            <template slot="title">
              <CIcon name="cil-image"/> ID card
            </template>

            <div class="d-flex flex-column col-xl-10 col-md-9">
              <template v-if="user">
                <h3>ID Card</h3>
                <cite>ID card images are used solely to validate the registered courier's identity.</cite>

                <div class="mt-3 articles">
                  <div>
                    <article class="mb-2">
                      <div class="article-wrapper">
                        <figure>
                          <input type="file" accept=".png, .jpg, .jpeg, .webp"  ref="identityFrontRef" style="display: none" @change="uploadFront" />
                          <expandable-image
                            :src="identityFront"
                            alt=""
                            v-if="identityFront"
                          />
                          <div v-else class="d-flex flex-column align justify-content-center align-items-center p-3" @click="$refs.identityFrontRef.click()">
                            <CIcon name="cil-plus" size="xl"/>
                            <cite class="mt-2 font-weight-light">No image is uploaded, please provide the front of ID card</cite>
                          </div>
                        </figure>
                        <div class="article-body">
                          <div class="d-flex flex-gap-1 mb-2 justify-content-between">
                            <CBadge color="info">Front</CBadge>
                            <div>
                              <button 
                                @click="$refs.identityFrontRef.click()" 
                                v-if="identityFront" 
                                type="button"
                                class="btn btn-primary mr-2"
                              >
                                  <CIcon name="cil-pencil"/>
                              </button>
                              <button 
                                @click="removeIDCard('FRONT')"
                                v-if="identityFront && isSuperAdmin && canDeleteIdentityFront" 
                                type="button"
                                class="btn btn-danger"
                              >
                                  <CIcon name="cil-trash"/>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                    <span v-if="identityFrontError" class="text-danger">{{ errors.identityFront }}</span>
                  </div>
                  <div>
                    <article class="mb-2">
                      <div class="article-wrapper">
                        <figure>
                          <input type="file" accept=".png, .jpg, .jpeg, .webp"  ref="identityBackRef" style="display: none" @change="uploadBack" />
                          <expandable-image
                            :src="identityBack"
                            alt=""
                            v-if="identityBack"
                          />
                          <div v-else class="d-flex flex-column align justify-content-center align-items-center p-3" @click="$refs.identityBackRef.click()">
                            <CIcon name="cil-plus" size="xl"/>
                            <cite class="mt-2 font-weight-light">No image is uploaded, please provide the back of ID card</cite>
                          </div>
                        </figure>
                        <div class="article-body">
                          <div class="d-flex flex-gap-1 mb-2 justify-content-between">
                            <CBadge color="info">Back</CBadge>
                            <div>
                              <button 
                                @click="$refs.identityBackRef.click()" 
                                v-if="identityBack" 
                                type="button"
                                class="btn btn-primary mr-2"
                              >
                                  <CIcon name="cil-pencil"/>
                              </button>
                              <button 
                                @click="removeIDCard('BACK')"
                                v-if="identityBack && isSuperAdmin && canDeleteIdentityBack" 
                                type="button"
                                class="btn btn-danger"
                              >
                                  <CIcon name="cil-trash"/>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                    <span v-if="identityBackError" class="text-danger">{{ errors.identityBack }}</span>
                  </div>
                </div>
              </template>
            </div>
          </CTab>

          <CTab :disabled="!form.user_id">
            <template slot="title">
              <div><CIcon name="cil-pen-nib"/> Contract</div>
            </template>
            <h3>Contract</h3>

            <div class="d-flex flex-column">
              <!-- Business Info -->
              <h5 class="pb-1 mt-1 mb-3" style="border-bottom: 2px solid #636f83;">Business Information</h5>

              <div v-if="hasActiveContract" class="alert alert-info p-2">
                <div class="vertical-center flex-gap-2">
                  <div class="h1 mb-0">🛈</div>
                  <div class="flex-grow-1 lead">Contract start date: {{ form.active_contract.start_date }}</div>
                  <CInputCheckbox custom label="Edit" :checked.sync="editContract" />
                </div>
              </div>

              <div :class="{ 'disable-item': hasActiveContract && !editContract }">
                <!-- contract_model, employment_type -->
                <div class="d-flex flex-column flex-md-row flex-gap-2 mb-2">
                  <CSelect
                    class="w-100"
                    label="Contract Model:"
                    placeholder="Please select.."
                    :options="[
                      { value: 'per_order', label: 'Per order' },
                      { value: 'hourly', label: 'Hourly' }
                    ]"
                    :value.sync="$v.form.active_contract.contract_model.$model"
                    @change="() => form.active_contract.employment_type = null"
                  />

                  <CSelect
                    class="w-100"
                    label="Employment Type:"
                    placeholder="Please select.."
                    :options="employmentTypeOptions"
                    :value.sync="$v.form.active_contract.employment_type.$model"
                  />
                </div>

                <!-- vat_number, company_name -->
                <div v-if="vatRequired" class="d-flex flex-column flex-md-row flex-gap-2 mb-2">
                  <CInput
                    class="w-100"
                    label="VAT (BTW) Number:"
                    placeholder="VAT number, if any"
                    :value.sync="$v.form.active_contract.vat_number.$model"
                    @focusout="fixVat"
                    :isValid="checkIfValid('active_contract.vat_number')"
                    @input="value => setNullIfEmpty(value, 'active_contract.vat_number')"
                  >
                    <template #append>
                      <CButton
                        color="info"
                        :disabled="spinners.btnCompanyInfo || !form.active_contract.vat_number || !isValidVat(form.active_contract.vat_number)"
                        @click="getCompanyInfo"
                      >
                        <CSpinner size="sm" v-if="spinners.btnCompanyInfo" />
                        Get Company Info
                      </CButton>
                    </template>
                  </CInput>

                  <CInput
                    class="w-100"
                    label="Company Name:"
                    placeholder="Company name"
                    :value.sync="$v.form.active_contract.company_name.$model"
                  />
                </div>

                <!-- Peppol Participant ID -->
                <div v-if="vatRequired" class="d-flex flex-column flex-md-row flex-gap-2 mb-2">
                  <CInput
                    class="w-100"
                    label="Peppol Participant ID:"
                    placeholder="Peppol Participant ID"
                    :lazy="false"
                    :value.sync="form.active_contract.peppol_participant_id"
                    readonly
                  >
                    <template #append>
                      <CButton
                        color="info"
                        :disabled="spinners.btnPeppol || !form.active_contract.vat_number || !isValidVat(form.active_contract.vat_number)"
                        @click="getPeppolParticipantId"
                      >
                        <CSpinner size="sm" v-if="spinners.btnPeppol" />
                        Get Peppol ID
                      </CButton>
                    </template>
                  </CInput>

                  <div class="w-100 d-none d-md-block"></div>
                </div>

                <!-- rate -->
                <div class="d-flex flex-column flex-md-row flex-gap-2 mb-2">
                  <CInput
                    class="w-100"
                    :label="`Rate for '${form.active_contract?.contract_model?.replace('_', ' ')}' contract model:`"
                    placeholder="0.00"
                    :value.sync="$v.form.active_contract.rate.$model"
                    type="number"
                    step="0.01"
                    pattern="^\d+(?:\.\d{1,2})?$"
                  >
                    <template #prepend-content><CIcon name="cil-euro"/></template>
                  </CInput>

                  <div class="w-100"></div>
                </div>
              </div>

              <!-- Contract History -->
              <h5 class="pb-1 my-3" style="border-bottom: 2px solid #636f83;">Contract History</h5>
              <ContractHistory :courierId="form.user_id?.toString()" />
            </div>
          </CTab>

          <CTab :disabled="!form.user_id">
            <template slot="title">
              <div><CIcon name="cil-lock-unlocked"/> Permissions</div>
            </template>
            <h3>Permissions</h3>
            <courier-permissions :permissions.sync="form.detail.permissions" />
          </CTab>

          <CTab :disabled="!form.user_id || hasFlexibleWorkingHours">
            <template slot="title">
              <div><CIcon name="cil-clock"/> Working Hours</div>
            </template>
            <h3>Working Hours</h3>
            <working-hours
              :workingHours.sync="workingHours"
              @validate="(invalid) => workingHoursInvalid = invalid"
            />
          </CTab>

          <CTab :disabled="!form.user_id || hasFlexibleWorkingHours">
            <template slot="title">
              <div><CIcon name="cil-mood-good"/> Custom Working Hours</div>
            </template>
            <h3>Custom Working Hours</h3>
            <custom-working-hours
              :customWorkingHours.sync="customWorkingHours"
              @validate="(invalid) => customWorkingHoursInvalid = invalid"
            />
          </CTab>

          <CTab>
            <template slot="title">
              <CIcon name="cil-notes"/> Registration Notes
            </template>

            <div class="d-flex flex-column col-xl-10 col-md-9">
              <template v-if="user">
                <h3>Registration Notes</h3>
                <cite v-if="validRegNotes">A courier registration has been received via our website. Please review and process the provided information to activate the courier's account.</cite>
                <cite v-else>There are no registration notes available for this courier.</cite>

                <div class="mt-3">
                  <ul v-if="validRegNotes">
                    <li v-if="user.detail?.registration_notes.selectedCity != null">Wants to work in: <strong>{{ user.detail?.registration_notes.selectedCity?.capitalize() }}</strong></li>
                    <li v-if="user.detail?.registration_notes.isFlexHours != null">Contract model:  <strong>{{ user.detail?.registration_notes.isFlexHours ? 'Per Order' : 'Hourly' }}</strong></li>
                    <li v-if="this.user.detail?.registration_notes.selectedShift != null">Working <strong>{{ shift }}</strong></li>
                    <li v-if="user.detail?.registration_notes.selectedVehicle != null">Want's to use <strong>{{ getTravelModeByType(user.detail?.registration_notes.selectedVehicle).label.toLowerCase() }}</strong> {{ user.detail?.registration_notes.licensePlate ? 'with license plate: ' : '' }} <strong v-if="user.detail?.registration_notes.licensePlate">{{ user.detail?.registration_notes.licensePlate }}</strong></li>
                  </ul>
                  <div v-else class="d-flex justify-content-center mt-5">
                    <CIcon name="cil-warning" class="warning-icon"/>
                  </div>
                </div>
              </template>
            </div>
          </CTab>

          <CTab :disabled="!form.user_id">
            <template slot="title">
              <CIcon name="cil-pen"/> Internal Notes
            </template>

            <div class="d-flex flex-column col-xl-10 col-md-9">
              <template v-if="user">
                <h3>Internal Notes</h3>
                <cite>These are administrative notes for internal use only. They are not visible to the courier.</cite>

                <!-- Add new note form -->
                <div class="mt-4 mb-4 p-3 bg-light rounded">
                  <h5>Add New Note</h5>
                  <CTextarea
                    rows="4"
                    placeholder="Add internal note about this courier..."
                    :value.sync="newInternalNote"
                    class="w-100 mb-2"
                  />
                  <small class="form-text text-muted mb-2">Maximum 5000 characters</small>
                  <CButton 
                    color="primary" 
                    size="sm"
                    :disabled="!newInternalNote.trim()"
                    @click="addInternalNoteToForm"
                  >
                    <CIcon name="cil-plus" size="sm" /> Add Note
                  </CButton>
                </div>

                <!-- Existing notes list -->
                <div v-if="form.detail.internal_notes.length > 0">
                  <h5>Notes ({{ form.detail.internal_notes.length }})</h5>
                  <div class="notes-list">
                    <div 
                      v-for="note in sortedInternalNotes" 
                      :key="note.id"
                      class="note-item p-3 mb-3 border rounded"
                    >
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <strong>{{ note.created_by_name }}</strong>
                          <small class="text-muted ml-2">
                            {{ $globalFunc.formatDateTime(note.created_at) }}
                          </small>
                          <CBadge v-if="note.is_new" color="success" class="ml-2">NEW</CBadge>
                        </div>
                        <CButton
                          v-if="isSuperAdmin || note.is_new"
                          color="danger"
                          size="sm"
                          variant="outline"
                          @click="removeInternalNoteById(note.id)"
                        >
                          <CIcon name="cil-trash" size="sm" />
                        </CButton>
                      </div>
                      <div class="note-content">
                        {{ note.content }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- No notes message -->
                <div v-else class="text-center mt-4">
                  <CIcon name="cil-notes" class="text-muted" size="3xl" />
                  <p class="text-muted mt-2">No internal notes have been added yet.</p>
                </div>

                <div v-if="hasUnsavedNotes" class="alert alert-warning mt-3">
                  <CIcon name="cil-warning" /> You have unsaved notes. Please submit the form to save all changes.
                </div>
              </template>
            </div>
          </CTab>
        </CTabs>
      </CForm>

      <CSpinner v-else color="primary" />
    </CCardBody>

    <CCardFooter class="sticky-bottom">
      <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem">
        <CButton
          color="primary"
          :disabled="spinners.loading || spinners.btnSubmit || !form.user_id"
          @click="submit()"
        >
          <CSpinner v-if="spinners.btnSubmit" size="sm" /> Submit
        </CButton>
        <CButton
          color="success"
          :disabled="!showUploadBtn"
          @click="uploadIdentity()"
        >
          <CSpinner v-if="spinners.btnUpload" size="sm" /> Upload ID Card
        </CButton>
      </div>
    </CCardFooter>

    <mc-spinner :opacity="0.8" v-show="spinners.btnSubmit || spinners.btnUpload" />
  </CCard>

</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { validationMixin } from "vuelidate"
import { helpers, required, requiredIf } from "vuelidate/lib/validators"
import _debounce from 'lodash/debounce';
import CourierPermissions from "@/components/Permissions/Courier.vue";
import WorkingHours from './WorkingHours'
import CustomWorkingHours from './CustomWorkingHours'
import ContractHistory from "@/components/Courier/ContractHistory";
import ExpandableImage from './ExpandableImage'
import { Role } from "../../../router/role";

const validIBAN = (value) => !helpers.req(value) || IBAN.isValid(value)

const hasHouseNumber = (address) => {
  var regex = /\d+/g;
  return regex.test(address);
}

export default {
  name: 'Courier',
  components: {
    'courier-permissions': CourierPermissions,
    'working-hours': WorkingHours,
    'custom-working-hours': CustomWorkingHours,
    ContractHistory,
    ExpandableImage
  },

  data() {
    return {
      activeTab: 0,
      isEdit: false,
      form: {
        user_id: null,
        hubs: [],
        vehicle: {
          mode: 'TWO_WHEELER',
          license_plate: null,
        },
        working_hours: [],
        detail: {
          reg_number: null,
          birth_date: null,
          phone_number: null,
          iban: null,
          account_holder_name: null,
          address: null,
          city: null,
          postal_code: null,
          country_id: null,

          is_dynamic_hub: true,

          permissions: [],
          internal_notes: []
        },
        active_contract:{
          contract_model: 'hourly',
          employment_type: 'employee',
          rate: null,
          vat_number: null,
          peppol_participant_id: null,
          company_name: null,
        }
      },

      workingHours: [],
      customWorkingHours: [],
      workingHoursInvalid: false,
      customWorkingHoursInvalid: false,

      user: null,
      users: [],
      hubs: [],

      spinners: {
        loading: false,
        searching: false,
        btnSubmit: false,
        btnCompanyInfo: false,
        btnPeppol: false,
        btnUpload: false,
      },
      unsavedChanges: false,
      editContract: false,

      // Google Maps API Autocomplete
      verified_address: '',
      autocomplete_address: null,

      // Vuelidate-error-extractor
      attributes: {
        user_id: 'User',
        detail: {
          reg_number: 'National registration number',
          birth_date: 'Birth date',
          phone_number: 'Phone number',
          iban: 'Bank account number (IBAN)',
          account_holder_name: 'Bank account holder name',
          address: 'Address',
          city: 'City',
          postal_code: 'Postal code',
          country_id: 'Country',
          is_dynamic_hub: 'Dynamic Hub',
        },
        vehicle: {
          mode: 'Travel mode',
        },
        active_contract: {
          contract_model: '[Contract] Contract model',
          employment_type: '[Contract] Employment type',
          rate: '[Contract] Rate for contract model',
          vat_number: '[Contract] VAT (BTW) number',
          company_name: '[Contract] Company name',
        }
      },

      // ID Card
      identityFront: '',
      identityBack: '',
      identityFrontError: false,
      identityBackError: false,
      canDeleteIdentityFront: false,
      canDeleteIdentityBack: false,
      selectedIdentityFront: {
        file: null,
        name: '',
        src: '',
        base64: '',
        uploaded: false,
      },
      selectedIdentityBack: {
        file: null,
        name: '',
        src: '',
        base64: '',
        uploaded: false,
      },
      identityMap: {
        FRONT: 'identityFront',
        BACK: 'identityBack'
      },
      showUploadBtn: false,
      
      // Internal Notes
      newInternalNote: '',
      courierLocation: null,
      courierNearestHub: null,
      courierLocationRefreshTimer: null,
    }
  },

  watch: {
    'hasFlexibleWorkingHours': function(newVal, oldVal) {
      // Eğer hasFlexibleWorkingHours true'dan false'a değiştiyse ve is_dynamic_hub true ise
      if (!newVal && oldVal && this.form.detail.is_dynamic_hub) {
        this.showDynamicHubAlert();
        this.form.detail.is_dynamic_hub = false;
      }
    },
    'selectedIdentityFront.file': function(newVal, oldVal) {
      if (this.selectedIdentityFront.uploaded) {
        this.canDeleteIdentityFront = true;
        this.showUploadBtn = false;
      } else {
        this.canDeleteIdentityFront = false;
        this.showUploadBtn = true;
      }
    },
    'selectedIdentityBack.file': function(newVal, oldVal) {
      if (this.selectedIdentityFront.uploaded) {
        this.canDeleteIdentityBack = true;
        this.showUploadBtn = false;
      } else {
        this.canDeleteIdentityBack = false;
        this.showUploadBtn = true;
      }
    },
  },

  mixins: [validationMixin],
  validations: {
    form: {
      user_id: { required },
      hubs: { },
      vehicle: {
        mode: { required },
        license_plate: { },
      },
      detail: {
        reg_number: { required },
        birth_date: { required },
        phone_number: { required },
        iban: { validIBAN },
        account_holder_name: {
          required: requiredIf(function (value) {
            return !!(value.iban);
          }),
        },
        address: { required, hasHouseNumber },
        city: { required },
        postal_code: { required },
        country_id: { required },

        is_dynamic_hub: { },
      },
      active_contract: {
        contract_model: { required },
        employment_type: { required },
        rate: { required },

        vat_number: {
          required: requiredIf(function (value) {
            return this.vatRequired && !!value.vat_number;
          }),
          checkVAT: function (value) {
            return this.isValidVat(value);
          },
        },

        company_name: {
          required: requiredIf(function () {
            return this.vatRequired;
          }),
        }
      }
    }
  },

  // Route koruması için
  beforeRouteLeave (to, from , next) {
    if (this.unsavedChanges === true) {
      const answer = window.confirm('Do you really want to leave? You have unsaved changes!')
      this.unsavedChanges = !answer;
      next(answer) // answer: true or false
    } else {
      next()
    }
  },

  async beforeMount() {
    this.searchUsers = _debounce(async (val) => {
      await this.getUsers(val);
    }, 500);

    await this.getHubs();

  },

  beforeDestroy() {
    this.stopLocationRefresh();
  },

  async mounted() {
    this.spinners.loading = true;
    await this.fetchTravelModes();
    this.spinners.loading = false;

    this.activeTab = this.$route.query.tab ? parseInt(this.$route.query.tab) : 0;

    // F5 veya başka bir siteye gitmeden önce 'Yaptığınız değişiklikler kaydedilmemiş olabilir.' koruması için
    window.onbeforeunload = () => this.unsavedChanges === true ? true : null;

    this.$watch('form', { handler: 'watchForm', deep: true });
    this.$watch('workingHours', { handler: 'watchForm', deep: true });
    this.$watch('customWorkingHours', { handler: 'watchForm', deep: true });

    if (this.itemId) {
      await this.getForm(this.itemId);
      if (this.user) {
        await this.getIDCard();
        this.initAutocomplete();
        this.setCourierLocationFrom(this.user);
        this.loadCourierLocation();
        this.startLocationRefresh();
      }
    } else {
      this.form.detail.permissions = this.$globalVar.courierPermissions.filter(p => p.default).pluck('name');
    }

    this.$watch('form.detail.country_id', function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.autocomplete_address.setComponentRestrictions({
          country: [this.country?.iso2]
        });
      }
    });

    setTimeout(() => {
      sessionStorage.setItem(`courier-form`, JSON.stringify(this.transformedForm));
      this.unsavedChanges = false;
    }, 500);
  },

  computed: {
    ...mapGetters(["errors"]),
    ...mapGetters('settings', ['getTravelModes', 'getTravelModeByType']),

    isValid() { return !(this.$v.form.$invalid)},
    isDirty() { return this.$v.form.$anyDirty },
    itemId() { return Number(this.$route.params.id) || null },
    transformedForm() {
      const { hubs, working_hours, detail, ...rest } = this.form;

      const permissionsMap = this.$globalVar.courierPermissions.reduce((acc, { name }) => {
        acc[name] = detail.permissions.includes(name);
        return acc;
      }, {});

      // Process internal notes - remove is_new flag and ensure proper structure
      const processedInternalNotes = detail.internal_notes.map(note => {
        const { is_new, ...cleanNote } = note;
        return cleanNote;
      });

      return {
        ...rest,
        hubs: this.form.hubs.join(','),
        working_hours: [...this.workingHours, ...this.customWorkingHours],
        detail: {
          ...detail,
          ...permissionsMap,
          internal_notes: processedInternalNotes,
          permissions: undefined // Remove the permission property
        },
      }
    },
    country() {
      return this.authCountries.find((item) => item.value == (this.form.detail.country_id || this.$store.state.countryId));
    },
    hasActiveContract() { return !!(this.form.active_contract.start_date) },
    hasFlexibleWorkingHours() { return this.form.active_contract?.contract_model === 'per_order' },
    vatRequired() {
      // Check if active_contract exists
      if (this.form.active_contract) {
          return this.form.active_contract.employment_type === 'self_employed';
      }
      return false;
    },
    employmentTypeOptions() {
      const model = this.form.active_contract?.contract_model;
      const baseOptions = [{ value: 'self_employed', label: 'Self Employed' }];

      if (model === 'per_order') {
        return [{ value: 'p2p', label: 'P2P' }, ...baseOptions];
      } else if (model === 'hourly') {
        return [{ value: 'employee', label: 'Employee' }, ...baseOptions];
      }

      return [];
    },
    shift() {
      if(this.user.detail?.registration_notes?.selectedShift != null) {
        switch (this.user.detail?.registration_notes.selectedShift) {
          case 'weekend':
            return 'only during the weekend';
          case 'weekday':
            return 'only during the week';
          case 'flex':
            return 'both during the week and weekend';
        }
      }
    },
    validRegNotes() {
      // Get the registration notes (could be null/undefined)
      const regNotes = this.user.detail?.registration_notes;
      
      // If it's null/undefined, return false
      if (!regNotes) return false;
      
      // Get the keys from the registration notes object
      const keys = Object.keys(regNotes);
      
      // If there are no keys, it's considered empty, so return false
      if (keys.length === 0) return false;
      
      // If all keys are either "identityFront" or "identityBack", return false
      if (keys.every(key => ['identityFront', 'identityBack'].includes(key))) {
        return false;
      }
      
      // Otherwise, there's at least one key that is not "identityFront"/"identityBack"
      return true;
    },
    hasUnsavedNotes() {
      return this.form.detail.internal_notes.some(note => note.is_new);
    },
    sortedInternalNotes() {
      return this.form.detail.internal_notes
        .slice() // Create a copy to avoid mutating the original array
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // Sort by date, newest first
    },
    canViewUserDetails() {
      return [Role.SuperAdmin, Role.Admin].some(role => this.authUserRoles?.includes(role));
    },
    currentCourierLocation() {
      return this.courierLocation || this.user?.location || null;
    },
    currentCourierNearestHub() {
      return this.courierNearestHub || this.user?.nearest_hub || null;
    },
    userDevices() {
      return Array.isArray(this.user?.usersdevices) ? this.user.usersdevices : [];
    },
    currentCourierLocationUrl() {
      const location = this.currentCourierLocation;

      if (!location) {
        return null;
      }

      if (location.google_maps_url) {
        return location.google_maps_url;
      }

      if (location.latitude && location.longitude) {
        return `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
      }

      return null;
    },
  },

  methods: {
    async showDynamicHubAlert() {
      await this.mSwal.fire({
        title: 'Dynamic Hub Disabled',
        text: 'The courier is now on an hourly contract (fixed hours). The dynamic hub feature has been automatically disabled.',
        icon: 'info',
        confirmButtonText: 'OK',
        showCancelButton: false,
      });
    },
    openIDCard() {
      this.$store.commit('set', ['idCardShow', true]);
    },
    ...mapActions('settings', ['fetchTravelModes']),
    
    checkIfValid (fieldName) {
      const fields = fieldName.split(".");
      const field = fields.length > 1
        ? this.$v.form[fields[0]][fields[1]]
        : this.$v.form[fieldName];

      if (!field.$dirty) {
        return null
      }
      return !(field.$invalid || field.$model === "");
    },

    validate() {
      this.$v.$touch()
    },

    watchForm(newV, oldV) {
      const initialState = sessionStorage.getItem(`courier-form`);
      const newState = JSON.stringify(this.transformedForm);
      this.unsavedChanges = initialState !== newState;
    },

    async getUsers(val) {
      if (val.length < 3) {
        return;
      }

      this.spinners.searching = true;
      await this.$axios.get(this.$backend.USERS.GET_ALL, {
        params: {
          search: val,
          'get-relations': false,
          'except-couriers': true,
        }
      })
        .then(({ data }) => {
          this.users = data.data.map((el) => {
            return { ...el, label: `i#${el.id} ${el.first_name} ${el.email}` };
          });
          this.spinners.searching = false;
        });
    },
    
    async getIDCard() {
      const registrationNotes = this.user.detail.registration_notes;
      
      // Return early if both identityFront and identityBack are missing
      if (!registrationNotes?.identityFront && !registrationNotes?.identityBack) {
        return;
      }

      this.spinners.loading = true;
      const names = [];

      if (registrationNotes?.identityFront) {
        names.push(`${registrationNotes.identityFront}.webp`);
      }
      
      if (registrationNotes?.identityBack) {
        names.push(`${registrationNotes.identityBack}.webp`);
      }

      try {
        const { data } = await this.$axios.post(this.$backend.COURIER.COURIERS.ID_CARD, {
          type: "couriers/ids",
          names: names
        });

        if (data.length !== 0) {
          let dataIndex = 0;
          if (registrationNotes?.identityFront) {
            this.identityFront = data[dataIndex].image;
            this.canDeleteIdentityFront = true;
            dataIndex++;
          }
          
          if (registrationNotes?.identityBack) {
            // Use the next element from data. It will be data[0] if only identityBack exists.
            this.identityBack = data[dataIndex]?.image;
            this.canDeleteIdentityBack = true;
          }
        }
      } catch (error) {
        this.$toast.error(`${error}`);
      } finally {
        this.spinners.loading = false;
      }
    },
    
    async removeIDCard(type) {
      // Determine which field to use.
      const field = this.identityMap[type];
      if (!field) return;
      
      // Get the identity value from the user data.
      const identityValue = this.user.detail.registration_notes?.[field];
      if (!identityValue) return;

      // Build the image name.
      const name = `${identityValue}.webp`;

      // Start the spinner.
      this.spinners.btnSubmit = true;

      try {
        // Make the DELETE request.
        const { data } = await this.$axios.delete(this.$backend.COURIER.COURIERS.ID_CARD, {
          data: {
            type: "couriers/ids",
            name,
            cid: this.user.id
          }
        });

        if (data.success) {
          this.removeIDCardImage(type);
        }
      } catch (error) {
        console.error(error);
        this.$toast.error(`${error}`);
      } finally {
        // Stop the spinner regardless of success or error.
        this.spinners.btnSubmit = false;
      }
    },

    removeIDCardImage(type) {
      const field = this.identityMap[type];
      if (!field) return;

      // Check if the field exists.
      if (!this.user.detail.registration_notes?.[field]) return;

      // Clear the corresponding component property.
      // For 'FRONT', this sets this.identityFront = ''; for 'BACK', this sets this.identityBack = '';
      this[`identity${type === 'FRONT' ? 'Front' : 'Back'}`] = '';

      // Remove the property from registration_notes.
      delete this.user.detail.registration_notes[field];
    },

    handleUserInput(user) {
      if (user === null) {
        this.form.user_id = null;
        this.form.detail.phone_number = null;
        this.form.detail.address = null;
        this.form.detail.city = null;
        this.form.detail.postal_code = null;
        this.form.detail.country_id = null;
        return;
      }

      this.form.user_id = user.id;
      this.form.detail.phone_number = user.mobile;
      this.form.detail.address = user.address;
      this.form.detail.city = user.city;
      this.form.detail.postal_code = user.postal_code;

      this.$nextTick(() => {
        this.initAutocomplete();
        this.form.detail.country_id = this.country.value;
      });
    },

    async getForm() {
      this.isEdit = true
      this.spinners.loading = true;

      await this.$axios.get(this.$backend.COURIER.COURIERS.SHOW.replace("{id}", this.itemId))
        .then(({data}) => {
          this.user = data;

          this.form = {
            user_id: data.id,
            hubs: data.hubs?.pluck('id'),
            vehicle: {
              mode: data.vehicle?.mode,
              license_plate: data.vehicle?.license_plate,
            },
            working_hours: data.future_working_hours,
            detail: {
              reg_number: data.detail.reg_number ?? '-',
              birth_date: data.detail.birth_date ?? '2000-01-01',
              phone_number: data.detail.phone_number ?? this.user.mobile,
              iban: data.detail.iban,
              account_holder_name: data.detail.account_holder_name,
              address: data.detail.address ?? this.user.address,
              city: data.detail.city ?? this.user.city,
              postal_code: data.detail.postal_code ?? this.user.postal_code,
              country_id: data.detail.country_id ?? this.country.value,

              is_dynamic_hub: data.detail.is_dynamic_hub,

              permissions: this.$globalVar.courierPermissions.filter(p => data.detail[p.name]).pluck('name'),
              internal_notes: data.detail.internal_notes || []
            },
            active_contract: {
              contract_model: data.active_contract?.contract_model ?? 'hourly',
              employment_type: data.active_contract?.employment_type ?? 'employee',
              rate: data.active_contract?.rate ?? 0,
              vat_number: data.active_contract?.vat_number,
              peppol_participant_id: data.active_contract?.peppol_participant_id,
              company_name: data.active_contract?.company_name ?? '-',
              start_date: data.active_contract?.start_date,
            },
          }

          this.workingHours = data.future_working_hours.filter((wh) => wh.day_of_week != null && !wh.custom_date);
          this.customWorkingHours = data.future_working_hours
            .filter((wh) => wh.day_of_week == null && wh.custom_date)
            .map(el => ({ ...el, invalid: false }));

          this.spinners.loading = false;
        })
        .catch((error) => {
          this.$toast.error(`${error.response.data.message}`);
          setTimeout(() => { this.$router.push({ name: 'Couriers' }) }, 3000);
        })
        .finally(() => { this.unsavedChanges = false })
    },

    async getHubs() {
      await this.$axios
        .get(this.$backend.COURIER.HUBS.GET_LIST)
        .then(({ data }) => this.hubs = data)
        .catch(() => this.hubs = [])
    },

    setCourierLocationFrom(courier) {
      this.courierLocation = courier?.location || null;
      this.courierNearestHub = courier?.nearest_hub || null;
    },

    async loadCourierLocation() {
      if (!this.itemId || !this.$backend?.COURIER?.COURIERS?.LOCATIONS) {
        return;
      }

      await this.$axios
        .get(this.$backend.COURIER.COURIERS.LOCATIONS)
        .then(({ data }) => {
          const couriers = Array.isArray(data) ? data : data?.data || [];
          const courier = couriers.find((item) => Number(item.id) === Number(this.itemId));

          if (courier) {
            this.setCourierLocationFrom(courier);
          }
        })
        .catch(() => undefined);
    },

    startLocationRefresh() {
      this.stopLocationRefresh();
      this.courierLocationRefreshTimer = setInterval(this.loadCourierLocation, 60000);
    },

    stopLocationRefresh() {
      if (this.courierLocationRefreshTimer) {
        clearInterval(this.courierLocationRefreshTimer);
        this.courierLocationRefreshTimer = null;
      }
    },

    formatCourierLocationAge(location) {
      if (!location || location.age_minutes === null || typeof location.age_minutes === 'undefined') {
        return 'unknown';
      }

      if (location.age_minutes < 1) {
        return 'just now';
      }

      return `${location.age_minutes} min ago`;
    },

    formatCourierDistance(meters) {
      if (meters === null || typeof meters === 'undefined') {
        return '-';
      }

      if (meters < 1000) {
        return `${Math.round(meters)} m`;
      }

      return `${(meters / 1000).toFixed(2)} km`;
    },

    maskedDeviceId(deviceId) {
      if (!deviceId) {
        return '-';
      }

      if (deviceId.length <= 8) {
        return deviceId;
      }

      return `${deviceId.slice(0, 4)}...${deviceId.slice(-4)}`;
    },

    formatDeviceTitle(device) {
      return device.device_model || device.device_type || `Device #${device.id}`;
    },

    formatDeviceSecondary(device) {
      return [device.manufacturer, device.device_os, device.device_type]
        .filter(Boolean)
        .join(' | ');
    },

    formatDeviceApp(device) {
      if (!device.app) {
        return 'Unknown app';
      }

      return [device.app.provider, device.app.domain || device.app.app_id]
        .filter(Boolean)
        .join(' - ');
    },

    formatDeviceSeenAt(device) {
      return device.last_active || device.updated_at || '-';
    },

    formatDeviceLocation(device) {
      return device.location || '';
    },

    async submit() {
      window.scrollTo(0, 0);

      if (!this.isValid) {
        this.validate();
        this.activeTab = 0;
        return;
      }

      if (this.workingHoursInvalid) {
        this.activeTab = 3;
        this.$toast.error('Check the working hours!')
        return;
      }

      if (this.customWorkingHoursInvalid) {
        this.activeTab = 4;
        this.$toast.error('Check the custom working hours!')
        return;
      }

      if (this.isEdit) {
        await this.update();
        // await this.uploadIdentity();
      } else {
        await this.store()
      }
    },

    async update() {
      this.spinners.btnSubmit = true

      await this.$axios.put(this.$backend.COURIER.COURIERS.UPDATE.replace("{id}", this.itemId), this.transformedForm)
        .then(({ data }) => {
          this.$toast.success(`${data.data.message}`);
          this.customWorkingHours = this.customWorkingHours.filter((wh) => !wh.deleted);
          this.form.active_contract = this.$globalFunc.extractKeys(data.data.courier.active_contract, ['contract_model', 'employment_type', 'rate', 'vat_number', 'peppol_participant_id', 'company_name', 'start_date']);

          // Update internal notes from response to clear is_new flags
          if (data.data.courier.detail && data.data.courier.detail.internal_notes) {
            this.form.detail.internal_notes = data.data.courier.detail.internal_notes;
          }

          sessionStorage.setItem(`courier-form`, JSON.stringify(this.transformedForm));
          this.unsavedChanges = false;
          this.editContract = false;
        })
        .catch((error) => {
          this.spinners.btnSubmit = false
          this.$toast.error(typeof this.errors === 'object'
            ? Object.values(this.errors).join()
            : this.errors
          );
        })
        .finally(() => {
          this.spinners.btnSubmit = false
        });
    },

    async store() {
      this.spinners.btnSubmit = true

      await this.$axios.post(this.$backend.COURIER.COURIERS.STORE, this.transformedForm)
        .then(({ data }) => {
          this.$toast.success(`${data.data.message}`);
          this.unsavedChanges = false

          setTimeout(() => {
            this.$router.push({ name: 'Couriers' })
          }, 2000)
        })
        .catch((error) => {
          this.spinners.btnSubmit = false
          this.$toast.error(typeof this.errors === 'object'
            ? Object.values(this.errors).join()
            : this.errors
          );
        })
        .finally(() => {
          this.spinners.btnSubmit = false
        });
    },

    setNullIfEmpty(value, path) {
      if (value.trim() === '') {
        const keys = path.split('.');
        const lastKey = keys.pop();
        const obj = keys.reduce((acc, key) => acc[key], this.form);
        obj[lastKey] = null;
      }
    },
    setNullIfFalse(value, path) {
      if (value === false) {
        const keys = path.split('.');
        const lastKey = keys.pop();
        const obj = keys.reduce((acc, key) => acc[key], this.form);
        obj[lastKey] = null;
      }
    },

    // VAT Number
    fixVat(event) {
      const fixedValue = this.$globalFunc.fixVAT(event.target.value);
      event.target.value = fixedValue;
    },
    isValidVat(vatNumber) {
      const result = this.$globalFunc.checkVAT(vatNumber, this.country.value);
      return result || !helpers.req(vatNumber) || false;
    },
    async getCompanyInfo() {
      this.spinners.btnCompanyInfo = true

      try {
        const { data } = await this.$axios.post(this.$backend.RESTAURANT.VAT_VALIDATION, {
          country_code: this.country?.iso2.toUpperCase(),
          vat_number: this.form.active_contract.vat_number,
        });

        if (data.response) {
          const name = data.response.name.replace(/\u00a0/g, ' ');
          const address = data.response.address.replace(/\u00a0/g, ' ').trim();

          const [firmType, ...firmName] = name.includes('|')
            ? name.split('|').map(part => part.trim())
            : name.split(' ');

          // this.form.active_contract.company_type = firmType;
          this.form.active_contract.company_name = firmName.join(" ");

          // address: "Zeven Septemberlaan 7\n3550 Heusden-Zolder"
          const [firmAddress, postcodeAndCity] = address.split('\n');
          const [firmPostcode, ...firmCity] = postcodeAndCity.split(' ');

          this.form.detail.address = firmAddress;
          this.form.detail.postal_code = firmPostcode;
          this.form.detail.city = firmCity.join(" ");

          this.$toast.success(`Valid VAT number`);
        } else {
          this.$toast.error(`Invalid VAT (BTW) number!`);
        }
      } catch (error) {
        this.$toast.error(error.response.data.message);
      } finally {
        this.spinners.btnCompanyInfo = false;
      }
    },
    async getPeppolParticipantId() {
      this.spinners.btnPeppol = true;

      try {
        const { data } = await this.$axios.post(
          this.$backend.COURIER.COURIERS.PEPPOL_LOOKUP.replace('{id}', this.itemId),
          {
            vat_number: this.form.active_contract.vat_number,
            country_code: this.country?.iso2?.toUpperCase(),
          },
        );

        if (data.status === 'success') {
          this.form.active_contract.peppol_participant_id = data.peppol_participant_id;
          sessionStorage.setItem(`courier-form`, JSON.stringify(this.transformedForm));
          this.unsavedChanges = false;

          if (data.peppol_participant_id) {
            this.$toast.success('Peppol participant ID has been fetched successfully.');
          } else {
            this.$toast.info('No Peppol participant ID found.');
          }
        } else {
          this.$toast.error('Failed to fetch Peppol participant ID.');
        }
      } catch (error) {
        console.error(error);
        this.$toast.error('Failed to fetch Peppol participant ID.');
      } finally {
        this.spinners.btnPeppol = false;
      }
    },

    // Google places
    initAutocomplete() {
      // Google Maps API Autocomplete: Place Autocomplete Address Form
      this.autocomplete_address = new google.maps.places.Autocomplete(
        document.getElementById("verify_address"),
        {
          componentRestrictions: { country: [this.country?.iso2] },
          fields: ["address_components", "geometry", "formatted_address"],
          types: ["address"],
        }
      );
      this.autocomplete_address.addListener("place_changed", this.setPlace);
    },
    setPlace() {
      // Get the place details from the autocomplete object.
      const place = this.autocomplete_address.getPlace();

      let street_number = "";
      let route = "";
      let postcode = "";
      let locality = "";

      // Get each component of the address from the place details,
      // and then fill-in the corresponding field on the form.
      // place.address_components are google.maps.GeocoderAddressComponent objects
      // which are documented at http://goo.gle/3l5i5Mr
      for (const component of place.address_components) {
        const componentType = component.types[0];

        switch (componentType) {
          case "street_number": {
            street_number = component.long_name;
            break;
          }

          case "route": {
            route = component.short_name;
            break;
          }

          case "postal_code": {
            postcode = `${component.long_name}${postcode}`;
            break;
          }

          case "postal_code_suffix": {
            postcode = `${postcode}-${component.long_name}`;
            break;
          }

          case "locality": {
            locality = component.long_name;
            break;
          }
        }
      }

      this.verified_address = place.formatted_address;

      this.form.detail.address = `${route} ${street_number}`;
      this.form.detail.postal_code = postcode.replace(/\D/g, "");
      this.form.detail.city = locality;
    },

    // Upload ID Card
    async uploadFront(event) {
      const files = event.target.files;
      const maxSizeInMB = 5;
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024; 
      this.errors.identityFront = '';
      this.identityFrontError = false;

      if(files.length > 0) {
        const file = files[0];
        const validFileTypes = ['image/png', 'image/jpeg','image/webp']; 
        if (!validFileTypes.includes(file.type)) {
          this.identityFrontError = true;
          this.errors.identityFront = "The front of the ID is invalid. Ensure that the file type is correct (only PNG, JPG, or JPEG allowed).";
          return;
        } else {
          this.identityFrontError = false;
          this.errors.identityFront = ""
        }

        if (file.size > maxSizeInBytes) {
          this.identityFrontError = true;
          this.errors.identityFront = "The file size for the front of the ID is too large. Maximum 5MB allowed.";
          return;
        } else {
          this.identityFrontError = false;
          this.errors.identityFront = ""
        }

        this.selectedIdentityFront.base64 = await this.fileToBase64(file);
        this.identityFront = this.selectedIdentityFront.base64;
        this.selectedIdentityFront.file = file;
        this.selectedIdentityFront.name = file.name;

        if(this.selectedIdentityFront.file != null) {
          this.selectedIdentityFront.src = URL.createObjectURL(this.selectedIdentityFront.file);
          this.selectedIdentityFront.uploaded = false;
        }
      }
    },

    async uploadBack(event) {
      const files = event.target.files;
      const maxSizeInMB = 5; 
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024; 
      this.errors.identityBack = '';
      this.identityBackError = false;

      if(files.length > 0) {
        const file = files[0];
        const validFileTypes = ['image/png', 'image/jpeg','image/webp']; 
        if (!validFileTypes.includes(file.type)) {
          this.identityBackError = true;
          this.errors.identityBack = "The back of the ID is invalid. Ensure that the file type is correct (only PNG, JPG, or JPEG allowed).";
          return;
        } else {
          this.identityBackError = false;
          this.errors.identityBack = "";
        }

        if (file.size > maxSizeInBytes) {
          this.identityBackError = true;
          this.errors.identityBack = "The file size for the back of the ID is too large. Maximum 5MB allowed.";
          return;
        } else {
          this.identityBackError = false;
          this.errors.identityBack = "";
        }

        this.selectedIdentityBack.base64 = await this.fileToBase64(file);
        this.identityBack = this.selectedIdentityBack.base64;
        this.selectedIdentityBack.file = file;
        this.selectedIdentityBack.name = file.name;

        if(this.selectedIdentityBack.file != null) {
          this.selectedIdentityBack.src = URL.createObjectURL(this.selectedIdentityBack.file);
          this.selectedIdentityBack.uploaded = false;
        }
      }
    },

    async uploadIdentity() {
      this.spinners.btnUpload = true;

      // Prepare an array of upload promises.
      const uploads = [];
      if (this.selectedIdentityFront.file !== null) {
        uploads.push(this.uploadToCDN());
      }
      if (this.selectedIdentityBack.file !== null) {
        uploads.push(this.uploadToCDN(true));
      }

      // Wait for all uploads to finish.
      await Promise.all(uploads);

      this.spinners.btnUpload = false;
    },

    async uploadToCDN(back = false) {
      // Determine which identity we're dealing with.
      const identity = back ? this.selectedIdentityBack : this.selectedIdentityFront;
      const identityKey = back ? 'identityBack' : 'identityFront';
      const nameSuffix = back ? 'Back' : 'Front';

      // Build the request body.
      const body = {
        name: `${this.user.first_name} ${nameSuffix}`,
        base64: identity.base64,
        fileName: identity.name,
        removeOld: false,
        oldFileName: ''
      };

      // If an image already exists, mark for removal.
      if (this.user.detail?.registration_notes?.[identityKey]) {
        body.removeOld = true;
        body.oldFileName = this.user.detail.registration_notes[identityKey];
      }

      // Convert the base64 image to a Blob.
      const blob = this.base64ToBlob(body.base64);

      // Build the FormData for the request.
      const formData = new FormData();
      formData.append('image', blob, body.fileName);
      formData.set('type', 'couriers/ids');
      formData.set('removeOld', body.removeOld);
      formData.set('oldFileName', body.oldFileName);
      formData.set('name', body.name);
      formData.set('cid', this.user.id);

      try {
        const { data } = await this.$axios.post(this.$backend.COURIER.COURIERS.UPLOAD, formData);
        if (data.status === 'success') {
          // Update the identity key with the new image.
          this.$set(this.user.detail.registration_notes, identityKey, data.data.image);

          // Update selectedIdentity'Front'or'Back' variable uploaded to true
          this[`selectedIdentity${back ? 'Back' : 'Front'}`].uploaded = true;
          this[`selectedIdentity${back ? 'Back' : 'Front'}`].file = null;
          this[`selectedIdentity${back ? 'Back' : 'Front'}`].base64 = '';
          this[`selectedIdentity${back ? 'Back' : 'Front'}`].name = '';
          this[`selectedIdentity${back ? 'Back' : 'Front'}`].src = '';
        }
      } catch (error) {
        console.error('Error uploading identity:', error);
        this[`selectedIdentity${back ? 'Back' : 'Front'}`].uploaded = false;
      }
    },

    base64ToBlob(base64) {
      const [header, data] = base64.split(',');
      const mimeString = header.match(/:(.*?);/)[1];
      const byteString = atob(data);
      const ia = Uint8Array.from(byteString, char => char.charCodeAt(0));
      return new Blob([ia], { type: mimeString });
    },

    async fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    // Internal Notes Methods
    addInternalNoteToForm() {
      if (!this.newInternalNote.trim()) return;

      const newNote = {
        id: 'new_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
        content: this.newInternalNote.trim(),
        created_by: this.authUser.id,
        created_by_name: this.authUser.first_name,
        created_at: new Date().toISOString(),
        is_new: true
      };

      this.form.detail.internal_notes.push(newNote);
      this.newInternalNote = '';
    },

    removeInternalNoteById(noteId) {
      const index = this.form.detail.internal_notes.findIndex(note => note.id === noteId);
      if (index === -1) return;
      
      const note = this.form.detail.internal_notes[index];
      
      if (note.is_new || confirm('Are you sure you want to delete this note?')) {
        this.form.detail.internal_notes.splice(index, 1);
      }
    },
  }
}
</script>

<style>
.tab-menu > div:first-child {
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  /* border-bottom: 1px solid #d8dbe0; */
}

form.courier-form .form-control,
form.courier-form option,
form.courier-form .vs__search,
form.courier-form .vs__dropdown-menu,
form.courier-form .vs__selected {
  font-size: 1.125rem;
  color: #9b0a0a;
  font-weight: 500;
}
form.courier-form .vs__dropdown-toggle {
  padding: 2px 0 6px;
}
form.courier-form label.c-switch {
  margin-top: 0.3rem;
}

.add-couriers-modal .modal-header {
  padding: 0.75rem !important;
}
.add-couriers-modal .modal-footer {
  padding: 0.25rem !important;
}
</style>

<style scoped>
.warning-icon {
  color: rgba(139, 139, 139, 0.134);
  width: 5rem !important;
  height: 5rem !important;
  font-size: 5rem !important;
}

.user-info {
  position: relative;
}
.user-info .button {
  opacity: 0;
  transition: opacity .35s ease;
  background: rgba(0, 0, 0, .3);
  text-align: center;
  position: absolute;
  width: 100%;
  border-radius: 25px;
}
.user-info .button a {
  text-align: center;
  color: white;
  z-index: 1;
}
.user-info:hover .button {
  opacity: 1;
}

.courier-location-summary {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.9rem;
}

.device-list-item + .device-list-item {
  margin-top: 0.5rem;
}

.device-primary-row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.75rem;
}

.device-meta {
  margin-top: 0.5rem;
}

section.address {
  background: #F8F0DF;
  padding: 1rem;
  border-radius: 0.5rem;
}

.articles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  margin-bottom: 1.5rem;
}

@media screen and (max-width: 480px) {
  .articles {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
}

article {
  --link-icon-translate: -3px;
  --link-icon-opacity: 0.5;

  position: relative;
  border-radius: 12px;
  box-shadow: none;
  transform-origin: center;
  transition: all 0.4s ease-in-out;
  overflow: hidden;
  outline: 1px solid #ebebeb;
}

.article-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

figure {
  margin: 0;
  padding: 0;
  aspect-ratio: 40 / 9;
  overflow: hidden;
  cursor: pointer;
}

article img {
  max-width: 100%;
}

.article-body {
  padding: 12px;
}

.article-body .badge-info {
  height: fit-content;
}

article a {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: #28666e;
}

article a:focus {
  outline: 1px dotted #28666e;
}

article a .icon {
  min-width: 24px;
  width: 24px;
  height: 24px;
  margin-left: 5px;
  transform: translateX(var(--link-icon-translate));
  opacity: var(--link-icon-opacity);
  transition: all 0.3s;
}

article:has(:hover, :focus) {
  --link-icon-translate: 0;
  --link-icon-opacity: 1;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 10px 20px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px;
}

/* Internal Notes Styles */
.notes-list {
  max-height: 500px;
  overflow-y: auto;
}

.note-item {
  background-color: #f8f9fa;
  transition: all 0.2s ease;
}

.note-item:hover {
  background-color: #e9ecef;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.note-content {
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.5;
}
</style>
